import {
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Observable ,  Subscription } from 'rxjs';
import * as _ from 'lodash';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';

import { Kit } from '../../models/kit.model';
import { CollapseGroupComponent } from 'shared/collapse/collapse-group/collapse-group.component';
import { BaseSegmentComponent } from '../base/_base-segment.component';
import { ProductPanelService } from '../../product-panel.service';
import { segmentsData } from '../../../product-details/segments-data-stub';
import { BrandService } from 'shared/brand/brand.service';
import { ItemDetailService } from 'shared/item-detail/item-detail.service';
import { PartNumberService } from './part-number.service';
import { SearchService } from 'shared/search/search.service';
import { Config } from 'shared/config/env.config';
import {mergeMap} from 'rxjs/operators';


@Component({
  moduleId: module.id,
  selector: 'kits-segment',
  templateUrl: 'kits.component.html',
  styleUrls: ['kits.component.scss'],
})
export class KitsComponent extends BaseSegmentComponent implements OnInit, OnDestroy {

  @ViewChildren(CollapseGroupComponent) collapseGroups: QueryList<CollapseGroupComponent>;

  headersFields: Array<string> = [
    'component_part_number.value', 'partTerminology.id', 'description.value'
  ];
  headersData: Array<any>;
  itemDetailsData: any = [];
  kitsHeadersMap: {};
  kitsFormGroup: UntypedFormGroup;
  loaded: boolean = false;
  descriptionSelect: Array<any> = [];
  availableDescTypes: Array<string> = [
   'SHO',
   'DES',
   'INV',
   'EXT',
   'ABR',
   'LAB '
  ];
  languageSelect: Array<any> = [];
  languages: Array<any> =
    [{
      'option': 'EN',
      'title': 'English'
    },
    {
      'option': 'ES',
      'title': 'Spanish'
    },
    {
      'option': 'FR',
      'title': 'French'
    }];
  partTypeObservables: Array<any> = [];
  subscription: Subscription;
  segmentName: string = 'kits';

  constructor(
    protected productPanelService: ProductPanelService,
    protected brandService: BrandService,
    protected itemDetailService: ItemDetailService,
    protected searchService: SearchService,
    protected route: ActivatedRoute,
    protected ref: ChangeDetectorRef,
    private partNumberService: PartNumberService,
    private fb: UntypedFormBuilder
  ) {
    super(productPanelService, brandService, itemDetailService, searchService, route, ref);
  }

  get kits(): UntypedFormArray {
    return this.kitsFormGroup.get('kits') as UntypedFormArray;
  }

  get displayAddNew(): Boolean {
    return this.loaded;
  }

  get segmentForm(): UntypedFormGroup {
    return this.kitsFormGroup;
  }

  ngOnInit() {
    this.kitsFormGroup = this.fb.group({
      kits: this.fb.array([])
    });
    this.subscribeToItemDetail();
  }

  ngOnDestroy() {
    this.partTypeObservables = [];
    this.descriptionSelect = [];
    this.languageSelect = [];
    super.ngOnDestroy();
  }

  // replace parent method:
  setBulkData() {
    this.setData(this.getBulkSegmentData(this.segmentName));
    this.loaded = true;
  }

  // replace parent method:
  handleItemDetail(itemDetail: any) {
    this.setData(itemDetail.kits);
    this.loaded = true;
  }

  setData(itemDetail: any = []) {
    let kitsSegments = segmentsData.kits;
    if (this.bulkEdit) {
      kitsSegments = this.getSegmentsWithPlaceholder(kitsSegments);
    }
    this.prepareData(kitsSegments, itemDetail);
    this.setKitsFormArray(kitsSegments, itemDetail);
  }

  prepareData(data: any, itemDetailsData: any) {
    this.itemDetailsData = itemDetailsData;
    this.headersData = _.filter(data, (item: any) => {
      return _.includes(this.headersFields, item.item_name);
    });
    // modifying data in object with item_name keys:
    // { 'component_brand': {title: '', field_map: ''}, 'part_type': {title: '', field_map: ''} }
    this.kitsHeadersMap = _.reduce(data, (memo: any, item: any) => {
      (<any>memo)[item.item_name] = item;
      return memo;
    }, {});
  }

  setKitsFormArray(data: Array<any>, itemDetailsData: any): void {
    const kitsFGs = _.map(itemDetailsData, (item: any) => {
      const kitsModel = new Kit();
      _.each(data, (kitItem: any) => {
        if (!kitItem.field_map) { return; }
        const value = _.get(item, kitItem.item_name, '');
        const validators = this.getItemValidators(_.get(kitItem, 'validation', {}));
        _.set(kitsModel, kitItem.field_map, new UntypedFormControl(value, validators));
      });

      this.addPartTypeObservable(kitsModel);

      return this.fb.group(kitsModel);
    });

    this.kitsFormGroup.setControl('kits', this.fb.array(kitsFGs));
  }

  onAddNewKitClick() {
    const kitsModel = new Kit();
    _.each(this.kitsHeadersMap, (item: any, key: string) => {
      if (!item.field_map) { return; }
      const validators = this.getItemValidators(item.validation);
      _.set(kitsModel, item.field_map, new UntypedFormControl('', validators));
    });

    this.addPartTypeObservable(kitsModel);

    kitsModel.soldSeparately.setValue('Y');
    this.kits.push(this.fb.group(kitsModel));
    this.descriptionSelect[this.kits.length] = [];
    this.languageSelect[this.kits.length] = [];

    _.delay(() => this.collapseGroups.last.toggle(), 100);
  }

  onPartNumberSelect(e: TypeaheadMatch, formGroup: UntypedFormGroup, index: number) {
    // populate data
    // change select items lists: this.descriptionSelect[index], this.languageSelect[index]
    // change brand and part terminology:
    // formGroup.patchValue({componentBrand: 'new_value', 'partType': 'new_value'})
    /*
     * Build the Part Type string
     */
    const partTypeCategory = _.get(e.item, 'partTerminology.category.name', '');
    const partTypeSubCategory = _.get(e.item, 'partTerminology.subcategory.name', '');
    let partType = partTypeCategory;
    if (partTypeSubCategory) {
      partType += '/' + partTypeSubCategory;
    }

    /*
     * Update the the form controls with the selected brand and part type
     */
    formGroup.patchValue({componentBrand: e.item.brandId, partType: partType});


    /*
     * Build Description Type Array
     */
     // a list of description type codes for this part
    const keys = _.keys(e.item.descriptions)  ;


    // an array of description type codes and description type text
    // This data comes from the data segments file.
    const partNumberDescs: Array<any> = this.getPartNumberDescs(keys);

    // populate the descriptionSelect for this part
    this.descriptionSelect[index] = _.map(partNumberDescs, (description: any) => {
      return {
        option: description.field_map.toUpperCase(),
        title: description.title,
        descriptions: e.item.descriptions[description.field_map.toUpperCase()]
      };
    });

    /*
     * Set the select values for the description type, description language and description
     */
    const formArray: any = this.kitsFormGroup.controls.kits;

    if (this.descriptionSelect[index].length) {

      // set the description type to the first value in the description type list
      const descType = this.descriptionSelect[index][0].option;
      formArray.at(index).get('descriptionType').setValue(descType);


      // set the selected description language based on the selected description above.
      const description: any = _.find(this.descriptionSelect[index], ['option', descType]);
      const descKeys = _.keys(description.descriptions);
      this.languageSelect[index] = _.filter(this.languages, (language: any) => {
        return (_.indexOf(descKeys, language.option) > -1);
      });

      if (this.languageSelect[index].length) {
        const descLang: any = this.languageSelect[index][0].option;
        formArray.at(index).get('descriptionLanguage').setValue(descLang);
        const desc: any = _.find(this.descriptionSelect[index], ['option', descType]);
        // If we have found a language, set the description
        formArray.at(index).get('description').setValue(desc.descriptions[descLang]);
      }
    } else {
      // If we don't have any descriptions available, clear out the selected
      formArray.at(index).get('descriptionType').setValue('');
      formArray.at(index).get('descriptionLanguage').setValue('');
      formArray.at(index).get('description').setValue('');
      this.languageSelect[index] = [];
    }
  }

  descriptionChanged(selectedItem: any, index: number) {
    /*
     * get the description selected and populate the select languages
     */
    const description: any = _.find(this.descriptionSelect[index], ['option', selectedItem.option]);
    const keys = _.keys(description.descriptions);
    this.languageSelect[index] = _.filter(this.languages, (_language: any) => {
      return (_.indexOf(keys, _language.option) > -1);
    });


    // Set the language to the first avialable
    const formArray: any = this.kitsFormGroup.controls.kits;
    const descType: string = formArray.at(index).get('descriptionType').value;
    if (this.languageSelect[index].length) {
        formArray.at(index).get('descriptionLanguage').setValue(this.languageSelect[index][0].option);
    } else {
        formArray.at(index).get('descriptionLanguage').setValue('');
    }
    const language: string = formArray.at(index).get('descriptionLanguage').value;

    // Set the description
    const desc: any = _.find(this.descriptionSelect[index], ['option', descType]);
    formArray.at(index).get('description').setValue(desc.descriptions[language]);
  }

  languageChanged(selectedItem: any, index: number) {
    const formArray: any = this.kitsFormGroup.controls.kits;
    // Get the selected description type to set the description
    const descType: string = formArray.at(index).get('descriptionType').value;

    const desc: any = _.find(this.descriptionSelect[index], ['option', descType]);
    // Set the description based on the description type and language
    formArray.at(index).get('description').setValue(desc.descriptions[selectedItem.option]);
  }

  getKitItem(item_name: string): any {
    return (<any>this.kitsHeadersMap)[item_name];
  }

  getKitThumbnail(partNumber: string): string {
    let thumbnail: string = Config.DEFAULT_PRODUCT_IMAGE;
    const brandRelationshipId = this.brandService.getBrand();
    if (partNumber && brandRelationshipId) {
      thumbnail = Config.BACKEND + 'parts/assets/part-number/' + brandRelationshipId + '/100?status=published&partNumber=' + partNumber;
    }
    return thumbnail;
  }

  getKitProperty(item_name: string, property: string): any {
    return (<any>this.kitsHeadersMap)[item_name][property];
  }

  getKitTitle(item_name: string): any {
    return this.getKitProperty(item_name, 'title');
  }

  getKitLookup(item_name: string): any {
    return this.getKitProperty(item_name, 'lookup');
  }

  getKitHint(item_name: string): any {
    return this.getKitProperty(item_name, 'hint_detail');
  }

  getKitExample(item_name: string): any {
    return this.getKitProperty(item_name, 'example');
  }

  getKitFieldMap(item_name: string): any {
    return this.getKitProperty(item_name, 'field_map');
  }

  addPartTypeObservable (kitsModel: any) {
    this.partTypeObservables.push(new Observable((observer: any) => {
        // Runs on every search
        observer.next(kitsModel.componentPartNumber.value);
      })
      .pipe(mergeMap((token: string) => {
        return this.partNumberService.search(this.brandService.getBrand(), token);
      })));
  }

  save(): Observable<any> {
    if (this.bulkEdit) {
      return this.putDataBulk(
        this.segmentName,
        this.kits.value,
        this.replaceFieldMapOnItemName(this.kits.value, segmentsData.kits)
      );
    } else {
      const { id } = this.route.snapshot.params;
      return this.putData(id, this.segmentName, this.kits.value);
    }
  }

  removeRows(rows: any): void {
    const indexes: Array<number> = [];

    _.each(rows, (row: any) => {
      indexes.push(row.index);
    });

    if (indexes.length) {
      _.pullAt(this.partTypeObservables, indexes);
      _.pullAt(this.descriptionSelect, indexes);
      _.pullAt(this.languageSelect, indexes);
    }
  }

  getPartNumberDescs(types: Array<string>): Array<any> {
    const partNumberDescs = _.filter(segmentsData.descriptions, (description: any) => {
      return (
        _.indexOf(this.availableDescTypes, description.field_map.toUpperCase()) > -1 &&
        _.indexOf(types, description.field_map.toUpperCase()) > -1
      );
    });
    return partNumberDescs;
  }

  openGroup(open: boolean, formGroup: UntypedFormGroup, index: any) {
    if (!open) { return; }
    // make api calls

    const token: string = formGroup.get('componentPartNumber').value;
    this.partNumberService.fetch(this.brandService.getBrand(), token)
      .subscribe((result) => {

          const keys = _.keys(result.descriptions);

          const partNumberDescs: Array<any> = this.getPartNumberDescs(keys);

          this.descriptionSelect[index] = _.map(partNumberDescs, (_description: any) => {
            return {
              option: _description.field_map.toUpperCase(),
              title: _description.title,
              descriptions: result.descriptions[_description.field_map.toUpperCase()]
            };
          });

          const descType = formGroup.get('descriptionType').value;
          const description: any = _.find(this.descriptionSelect[index], ['option', descType]);
          if (description) {
            const langKeys = _.keys(description.descriptions);
            this.languageSelect[index] = _.filter(this.languages, (language: any) => {
              return (_.indexOf(langKeys, language.option) > -1);
            });
          }

      });
  }
}
